import { Box, Divider, Typography } from '@mui/material';
import darkTheme from '@shared/themes/darkTheme';
import React, { useState } from 'react';
import { useWidgetStore } from '../../../common/store/AppStore';
import { isLadderInfoEmpty, LadderInfo, LadderTabConfig, LiveLadderData } from './LadderTypes';
import { LadderStore } from './context/LadderStore';
import { LadderTabOptions } from './LadderTabOptions';
import { Side } from '@protos/trading';

export type LadderExecutionFunctions = {
  onLimitClick: (price: number, side: Side | undefined) => void;
  onFillOrKillClick: (price: number, side: Side) => void;
};

export type LadderTabProps = LadderInfo &
  LadderExecutionFunctions & {
    error?: string;
    visible?: boolean;
    widgetId: string;
  };

type LadderRowProps = LadderExecutionFunctions & {
  price: number;
  quantity: number;
  index: number;
  fairValueIndex: number;
  totalQuantity: number;
  orderAmount: number;
  max: number;
  side: Side | undefined;
  onMouseOver: (index: number) => void;
  onMouseOut: (index: number) => void;
  lastHighlightedRow: number;
  config: LadderTabConfig;
};

type LadderCellProps = React.PropsWithChildren &
  LadderRowProps & {
    flipSide?: boolean;
    rowHovered: boolean;
    hasData: boolean;
    style?: React.CSSProperties;
    disableIfSideFlipped?: boolean;
    config: LadderTabConfig;
  };

const sideToColorMap = {
  buy: darkTheme.palette.background.buyHover,
  sell: darkTheme.palette.background.sellHover,
  nodata: undefined,
};

const LadderCell = (props: LadderCellProps) => {
  let colour = props.rowHovered ? darkTheme.palette.background.lightHover : darkTheme.palette.background.lighter;

  if (props.flipSide && !props.rowHovered) {
    colour = darkTheme.palette.background.default;
  }

  return (
    <div
      style={{
        backgroundColor: colour,
        textAlign: 'center',
        width: '6em',

        ...props.style,
      }}
    >
      {props.flipSide && props.disableIfSideFlipped ? '' : props.children}
    </div>
  );
};

const QuantityCell = (props: LadderCellProps) => {
  const { quantity, max, rowHovered, side } = props;

  const isBidRow = side === Side.BUY;
  const isAskRow = side === Side.SELL;
  const barWidth = `${(quantity / max) * 100}%`;

  const quantityStyle: React.CSSProperties = {
    zIndex: 2,
    position: 'absolute',
    width: '100%',
    height: '1em',
    top: 0,
    fontWeight: 200,
  };

  const barStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    width: barWidth,
    height: '100%',
    zIndex: 0,
    opacity: 0.4,
    transition: 'width 0.3s ease-out',
  };

  if (isBidRow) {
    barStyle.right = 0;
    barStyle.backgroundColor = rowHovered ? darkTheme.palette.background.buy : darkTheme.palette.background.buyHover;
    quantityStyle.textAlign = 'right';
    quantityStyle.paddingRight = '1em';
  } else if (isAskRow) {
    barStyle.left = 0;
    barStyle.backgroundColor = rowHovered ? darkTheme.palette.background.sell : darkTheme.palette.background.sellHover;
    quantityStyle.textAlign = 'left';
    quantityStyle.paddingLeft = '1em';
  }

  return (
    <LadderCell {...props} style={{ width: '8em' }} disableIfSideFlipped={true}>
      <div style={{ position: 'relative', height: '100%' }}>
        <div style={barStyle} />
        <div style={quantityStyle}>{props.quantity}</div>
      </div>
    </LadderCell>
  );
};

const ActionCell = (props: LadderCellProps) => {
  const [isCellHovered, setIsCellHovered] = useState<boolean>(false);

  const onActionCellHover = () => {
    setIsCellHovered(true);
  };

  const onActionCellLeave = () => {
    setIsCellHovered(false);
  };

  const { side, flipSide, orderAmount, price, onLimitClick, onFillOrKillClick, config } = props;

  let action = '';

  if (side == Side.BUY) {
    if (flipSide) {
      action = 'SELL'; // HIT
    } else {
      action = 'BUY';
    }
  } else {
    if (flipSide) {
      action = 'BUY'; // LIFT
    } else {
      action = 'SELL';
    }
  }

  const disabled = flipSide && !config.allowFillOrKill;
  const message = action + ' ' + orderAmount;
  const text = isCellHovered && !disabled ? message : '';
  let backgroundColor = sideToColorMap[side as Side];

  let sideForFOK: Side = Side.BUY;
  if (flipSide) {
    if (side === Side.BUY) {
      sideForFOK = Side.SELL;
    } else {
      sideForFOK = Side.BUY;
    }

    backgroundColor = sideToColorMap[sideForFOK];
  }

  const cellStyle = { ...props.style, flexGrow: flipSide ? 2 : 1 };

  const setColour = !flipSide || (flipSide && config.allowFillOrKill);

  if (setColour && isCellHovered) {
    cellStyle.backgroundColor = backgroundColor;
  }

  return (
    <LadderCell {...props} style={cellStyle}>
      <div
        style={{ width: '100%', height: '100%', cursor: disabled ? 'default' : 'pointer' }}
        onMouseEnter={() => onActionCellHover()}
        onMouseLeave={() => onActionCellLeave()}
        onClick={() => {
          if (!side || disabled) {
            return;
          }
          if (flipSide) {
            onFillOrKillClick(price, sideForFOK);
          } else {
            onLimitClick(price, side);
          }
        }}
      >
        {text}
      </div>
    </LadderCell>
  );
};

export const LadderRow = (props: LadderRowProps) => {
  const { price, side, index, onMouseOver, onMouseOut, lastHighlightedRow } = props;

  const isBidRow = side === Side.BUY;
  const isAskRow = side === Side.SELL;
  const hasData = side !== undefined;

  // TODO: need to say whether things are bids or asks based on where they are in the ladder,
  // not just whether they have data

  const hovered = index === lastHighlightedRow;

  return (
    <Box
      sx={{
        width: 'calc(100%)',
        display: 'flex',
        justifyContent: 'center',
        lineHeight: '1.6em',
        gap: '1px',
      }}
      onMouseEnter={() => onMouseOver(props.index)}
      onMouseLeave={() => onMouseOut(props.index)}
    >
      <div style={{ flexGrow: 1, display: 'flex' }}>
        <ActionCell {...props} flipSide={isAskRow} hasData={hasData} rowHovered={hovered} style={{ flexGrow: 1, flexShrink: 1 }} />
        <QuantityCell {...props} flipSide={isAskRow} hasData={hasData} rowHovered={hovered} />
      </div>

      <LadderCell {...props} flipSide={false} hasData={hasData} rowHovered={hovered} style={{ justifySelf: 'center' }}>
        {price.toFixed(2)}
      </LadderCell>

      <div style={{ flexGrow: 1, display: 'flex' }}>
        <QuantityCell {...props} flipSide={isBidRow} hasData={hasData} rowHovered={hovered} />

        <ActionCell {...props} flipSide={isBidRow} hasData={hasData} rowHovered={hovered} style={{ flexGrow: 1, flexShrink: 1 }} />
      </div>
    </Box>
  );
};

// const LadderContainer = () => {}

export const LadderTab = (props: LadderTabProps) => {
  const [lastHighlightedRow, setLastHighlightedRow] = useState<number>(-1);

  const { index, widgetId } = props;
  const { liveData, orderAmount } = useWidgetStore(props.widgetId) as LadderStore;

  if (isLadderInfoEmpty(props)) {
    return <div style={{ padding: '1em' }}></div>;
  }

  let ladder: LiveLadderData | undefined = undefined;

  if (liveData.length > index) {
    ladder = liveData[index];
  }

  return (
    <div style={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
      <div
        style={{
          padding: '1em 0',
          display: 'flex',
          flexGrow: 1,
          justifyContent: 'center',
          alignContent: 'center',
          flexWrap: 'wrap',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {!ladder || ladder.ladder.length === 0 ? (
            <Typography variant={'h4'} sx={{ textAlign: 'center' }}>
              NO DATA FOR {props.label}
            </Typography>
          ) : (
            ladder.ladder
              .sort((a, b) => (a.price > b.price ? -1 : 1))
              .filter(r => {
                return r.quantity > 0 || props.config.showAllTicks;
              })
              .map(({ price, quantity, side }, index) => {
                return (
                  <LadderRow
                    key={index}
                    price={price}
                    quantity={quantity}
                    index={index}
                    fairValueIndex={ladder?.fairValueIndex || 0}
                    totalQuantity={ladder?.total || 0}
                    max={ladder?.max || 0}
                    orderAmount={orderAmount}
                    side={side}
                    onMouseOver={(id: number) => setLastHighlightedRow(id)}
                    onMouseOut={(id: number) => {}}
                    onLimitClick={props.onLimitClick}
                    onFillOrKillClick={props.onFillOrKillClick}
                    lastHighlightedRow={lastHighlightedRow}
                    config={props.config}
                  />
                );
              })
          )}
        </div>
      </div>
      <Divider />
      <LadderTabOptions widgetId={widgetId} tabIndex={index} />
    </div>
  );
};
